import React, { createContext, useContext, useMemo, useState } from 'react';
import { useApplicationContext } from '@/context/v1/Application/context';
import type { OWASReport } from '@/core/domain/owas-report';
import type { Context, Methods, States, Parameters } from './types';

interface OWASProviderProps {
	children: JSX.Element;
	defaultParameters?: Partial<Parameters>;
	onClose: () => void;
}

const OWASReportContext = createContext<Context>({} as Context);

export function OWASProvider({ children, defaultParameters = {}, onClose }: Readonly<OWASProviderProps>) {
	const { organization, company } = useApplicationContext();

	const initialParameters = useMemo<Parameters>(
		() => ({
			organization_id: defaultParameters.organization_id ?? organization.id,
			company_id: defaultParameters.company_id ?? company.id,
			file_id: defaultParameters.file_id ?? ''
		}),
		[defaultParameters.company_id, defaultParameters.file_id, defaultParameters.organization_id, company.id, organization.id]
	);

	const [data, setData] = useState<OWASReport>();
	const [currentStep, setCurrentStep] = useState<number>(0);
	const [parameters, setParameters] = useState<Parameters>(initialParameters);
	const [, setErrorMessage] = useState<string | undefined>(undefined);

	function handleSetData(value: OWASReport): void {
		setData(value);
	}

	function handleNextStep(): void {
		setCurrentStep((prev) => prev + 1);
	}

	function handlePreviousStep(): void {
		setCurrentStep((prev) => (prev > 0 ? prev - 1 : 0));
	}

	function handleSetStep(step: number): void {
		setCurrentStep(step);
	}

	function handleSetError(message: string): void {
		setErrorMessage(message);
		setTimeout(() => {
			setErrorMessage(undefined);
		}, 6000);
	}

	function handleOnSetParameters(values: Parameters): void {
		setParameters(values);
	}

	function handleClose(): void {
		setCurrentStep(0);
		setData(undefined);
		onClose();
	}

	const states: States = {
		currentStep,
		parameters,
		data
	};

	const methods: Methods = {
		handleSetData,
		handleNextStep,
		handleSetStep,
		onError: handleSetError,
		handlePreviousStep,
		handleOnSetParameters,
		handleClose
	};

	const context: States & Methods = {
		...states,
		...methods
	};

	return <OWASReportContext.Provider value={context}>{children}</OWASReportContext.Provider>;
}

export function useOWASContext() {
	const context = useContext(OWASReportContext);
	return context;
}
